import React, { useState, useEffect, useContext } from 'react';
import { Props } from './props';
import { DetailRenderer, RedeemReward, ShowPageWrapper, SomethingWentWrong } from '~/components';
import { useApiUrl, useCustom, useNavigation, useOne, useTranslate } from '@refinedev/core';
import { resourceNames } from '~/resources/resource-names';
import { BsChevronUp } from 'react-icons/bs';
import {
  Button,
  Card,
  Checkbox,
  IconButton,
  Tabs,
  Tag,
  Dialog,
} from '@scalingworks/react-admin-ui';
import { customerFields, CustomerStatusTagColor } from '../customer-resource';
import { Customer, Membership, CustomerStatus } from '~/api';
import { LinkToDetails, ResourceField, useGraphQLClient } from '@scalingworks/refine-react-admin';
import { formatFullName, sortUserAddress } from '~/resources/helpers';
import { FiEdit3 } from 'react-icons/fi';
import { MembershipBar } from './membership';
import { CustomerOrder } from './order';
import { CustomerPointTransactions } from './point-transactions';
import { AddressContent } from './general/address';
import { CustomerRedemptionHistory } from './redemption-history';
import { CustomerWallet, WalletDashboard } from './wallet';
import dayjs from 'dayjs';
import { FullDateFormat } from '~/config/constant';
import camelCase from 'lodash/camelCase';
import startCase from 'lodash/startCase';
import { useOutletsCache } from '~/providers/Cache/useCache';
import {
  useGetActiveCardFromCustomerByUserIdQuery,
  useGetCustomerByReferralCodeQuery,
} from '~/generated/hooks';
import { useQueryClient } from '@tanstack/react-query';
import { AddStampsModal } from '~/components/StampCard/AddStampsModal';
import { FiPlus, FiCoffee } from 'react-icons/fi';
import { FaStamp } from 'react-icons/fa';
import { isValidStampCard, getStampCardProps, canAddStamps } from '../helpers/stamp-card-helpers';
import { ChannelContext } from '~/providers/Channel/context';
import StampCardIframe from '~/components/StampCard/StampCardIframe';

export const CustomerShowPage: React.FC<Props> = (props) => {
  const { resourceId: customerId } = props;

  // ======================= HOOKS
  const t = useTranslate();
  const navigateTo = useNavigation();
  const apiUrl = useApiUrl();
  const { getOutletById } = useOutletsCache();
  const queryClient = useQueryClient();

  // ======================= STATES
  const [showGeneral, setShowGeneral] = useState(true);
  const [showOrder, setShowOrder] = useState(true);
  const [showLoyalty, setShowLoyalty] = useState(true);
  const [showStampCard, setShowStampCard] = useState(true);
  const [openRedeemReward, setOpenRedeemReward] = useState(false);
  const [openAddStampsModal, setOpenAddStampsModal] = useState(false);
  const [showStampCardModal, setShowStampCardModal] = useState(false);
  // NOTE: a bit hackish way to make the loyalty listing below refetch after actions done.
  const [membershipFetch, setMembershipFetch] = useState<number>(1);

  // ======================= API
  const {
    data: customer,
    isLoading,
    refetch,
  } = useOne<Customer>({
    id: customerId,
    resource: resourceNames.customer,
    metaData: {
      fields: [
        ...customerFields,
        {
          addresses: [
            'id',
            'defaultBillingAddress',
            'defaultShippingAddress',
            'fullName',
            'phoneNumber',
            'streetLine1',
            'streetLine2',
            'postalCode',
            'city',
            'province',
            { customFields: ['latitude', 'longitude'] },
            { country: ['name'] },
          ],
          orders: [{ items: ['id'] }],
          user: ['id'],
        },
      ] as ResourceField<Customer>[],
    },
  });

  const { data: referralBy, refetch: refetchReferralBy } = useGetCustomerByReferralCodeQuery(
    { referralCode: customer?.data?.customFields?.referralCodeApplied || '' },
    {
      enabled: !!customer?.data?.customFields?.referralCodeApplied,
      queryKey: [
        'customerByReferralCode',
        customer?.data?.customFields?.referralCodeApplied,
        customerId,
      ],
      staleTime: 0,
      keepPreviousData: false,
    }
  );

  // Fetch stamp cards for the customer
  const { data: stampCardsData, refetch: refetchStampCards } =
    useGetActiveCardFromCustomerByUserIdQuery(
      { userId: customer?.data?.user?.id || '' },
      {
        enabled: !!customer?.data?.user?.id,
        queryKey: ['customerStampCards', customer?.data?.user?.id],
        staleTime: 0,
      }
    );

  const { data: membership, refetch: refetchMembership } = useCustom<Membership>({
    url: apiUrl,
    method: 'get',
    metaData: {
      fields: [
        'pointBalance',
        { tierProgress: [{ currentTier: ['id'] }] },
      ] as ResourceField<Membership>[],
      operation: 'getMembership',
      variables: {
        userId: {
          value: customer?.data?.user?.id,
          type: 'String!',
        },
      },
    },
    queryOptions: {
      enabled: !!customer?.data?.user?.id,
    },
  });

  // ======================= VARIABLES
  const { firstName, lastName, emailAddress, phoneNumber, customFields, user, orders } =
    customer?.data || {};
  const fullName = formatFullName(firstName, lastName);
  const {
    status,
    dateOfBirth,
    gender,
    pay2winOn,
    signupThrough,
    referralCode,
    referralCodeApplied,
    preferredOutletId,
    isProfileComplete,
  } = customFields || {};
  const sortedAddress = sortUserAddress(customer?.data?.addresses || []);
  const firstStampCard = stampCardsData?.getActiveCardFromCustomerByUserId;

  // Use helper functions to process stamp card data
  const stampCardProps = getStampCardProps(firstStampCard);
  const canAddStampsToCard = canAddStamps(firstStampCard);

  // ======================= CONTEXT
  const { channel } = useContext(ChannelContext);
  const isWalletEnabled = channel?.customFields?.storeCreditEnabled === true;

  // ======================= EVENTS
  const refetchAll = () => {
    refetch();
    refetchMembership();
    refetchReferralBy();
    refetchStampCards();
    setMembershipFetch((prev) => prev + 1);
  };

  // ======================= EFFECTS
  useEffect(() => {
    if (customerId) {
      queryClient.removeQueries(['customerByReferralCode']);
    }
  }, [customerId, queryClient]);

  // ======================= VIEWS
  // const notificationRender = () => {
  //   const renderer = {};
  //   Object.keys(customerPreferenceObj).forEach((key) => {
  //     Object.assign(renderer, { [key]: () => notificationCheck(customerPreferenceObj[key]) });
  //   });

  //   return renderer;
  // };
  // const notificationCheck = (on: boolean) => {
  //   return (
  //     <span>
  //       <Checkbox checked={on} disabled />
  //     </span>
  //   );
  // };

  if (!isLoading && !customerId) return <SomethingWentWrong />;
  return (
    <div className="overflow-y-scroll">
      {openRedeemReward && (
        <RedeemReward
          open
          setOpen={setOpenRedeemReward}
          customerName={fullName}
          userId={user?.id || ''}
          pointBalance={membership?.data?.pointBalance}
          onCompleted={refetchAll}
          currentTierId={membership?.data?.tierProgress?.currentTier?.id}
        />
      )}

      {openAddStampsModal && isValidStampCard(firstStampCard) && (
        <AddStampsModal
          open={openAddStampsModal}
          onClose={() => setOpenAddStampsModal(false)}
          userId={user?.id || ''}
          stampCardId={firstStampCard.id}
          templateId={firstStampCard.template?.id}
          currentStamps={firstStampCard.currentStamps ?? firstStampCard.totalStampsEarned ?? 0}
          totalStampsRequired={firstStampCard.totalStampsRequired ?? 10}
          customerName={fullName}
          onSuccess={refetchAll}
        />
      )}

      <ShowPageWrapper resourceName={resourceNames.customer} loading={isLoading} title={fullName}>
        <main className="flex flex-col space-y-8">
          {/* Membership */}
          <MembershipBar
            loading={isLoading}
            userId={user?.id || ''}
            onMutationComplete={refetchAll}
            customerId={customerId}
            payToWinDate={pay2winOn}
            fetchIndex={membershipFetch}
          />

          {/* Stamp Card - Clean Consistent Design */}
          {stampCardProps?.data && (
            <div className="bg-white rounded-lg p-6 border border-gray-200 shadow-sm">
              {/* Header */}
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center">
                  <FaStamp className="text-gray-600" size={20} />
                </div>
                <h3 className="text-lg font-semibold text-gray-800">Stamp Card</h3>
              </div>

              {/* Progress and Buttons Row */}
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <p className="text-sm text-gray-500 mb-3">Progress</p>

                  {/* Progress Display */}
                  <div className="space-y-4">
                    {/* Large Number Display */}
                    <div className="flex items-baseline gap-2">
                      <span className="text-3xl font-bold text-gray-900">
                        {stampCardProps?.data?.totalStampsEarned || 0}
                      </span>
                      <span className="text-lg text-gray-400">/</span>
                      <span className="text-xl font-semibold text-gray-600">
                        {stampCardProps?.data?.totalStampsRequired || 10}
                      </span>
                      <span className="text-sm text-gray-500 ml-2">stamps</span>
                    </div>

                    {/* Progress Bar */}
                    <div className="space-y-2">
                      <div className="w-full h-2.5 bg-gray-200 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-primary-600 transition-all duration-500 ease-out rounded-full"
                          style={{
                            width: `${Math.min(
                              ((stampCardProps?.data?.totalStampsEarned || 0) /
                                (stampCardProps?.data?.totalStampsRequired || 10)) *
                                100,
                              100
                            )}%`,
                          }}
                        />
                      </div>

                      {/* Status Text */}
                      <div className="flex items-center justify-between">
                        {stampCardProps?.data?.expiresAt && (
                          <span className="text-xs text-gray-500 flex items-center gap-1">
                            <svg
                              className="w-3 h-3"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                              />
                            </svg>
                            Expires {dayjs(stampCardProps.data.expiresAt).format('D MMM YYYY')}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex gap-3">
                  {canAddStampsToCard && (
                    <Button
                      variant="solid"
                      size="sm"
                      onClick={() => setOpenAddStampsModal(true)}
                      className="flex items-center gap-2"
                    >
                      <FiPlus size={16} />
                      Add Stamps
                    </Button>
                  )}
                  <Button
                    variant="default"
                    size="sm"
                    onClick={() => setShowStampCardModal(true)}
                    className="flex items-center gap-2"
                  >
                    Preview Stamp Card
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Stamp Card Detail Modal - Compact */}
          <Dialog.Root open={showStampCardModal} onOpenChange={setShowStampCardModal}>
            <Dialog.Portal>
              <Dialog.Overlay />
              <Dialog.Content className="fixed top-1/3 w-[28rem] max-w-[90vw] overflow-x-hidden">
                <Dialog.Title>{fullName}'s Stamp Card</Dialog.Title>
                <Dialog.CloseButton />

                <div className="py-4">
                  {/* Compact stamp card view */}
                  <div className="transform scale-90 origin-top">
                    <StampCardIframe
                      data={stampCardProps?.data}
                      theme={stampCardProps?.data?.template?.metadata?.theme}
                      showPreviewLabel={true}
                    />
                  </div>

                  {/* Minimal details */}
                  <div className="mt-4 text-sm text-gray-600 text-center">
                    {stampCardProps?.data?.expiresAt && (
                      <p>Valid until {dayjs(stampCardProps.data.expiresAt).format('D MMM YYYY')}</p>
                    )}
                  </div>
                </div>
              </Dialog.Content>
            </Dialog.Portal>
          </Dialog.Root>

          {/* Wallet Dashboard - Moved to top with new creative UI */}
          {isWalletEnabled && (
            <WalletDashboard
              customerId={customerId}
              loading={isLoading}
              fetchIndex={membershipFetch}
            />
          )}

          <Card className="relative z-0">
            <Card.Header
              className="font-bold cursor-pointer"
              bordered
              onClick={() => setShowGeneral(!showGeneral)}
            >
              <section className="flex flex-row items-center justify-between">
                <h1>{t('common.general', undefined, 'General')}</h1>
                <div className={`flex flex-row space-x-2`}>
                  {/* TODO: refactor these collapsible into a reusable wrapper */}
                  <BsChevronUp
                    size={20}
                    className={`transition-transform ${showGeneral ? 'rotate-180' : 'rotate-0'}`}
                  />
                </div>
              </section>
            </Card.Header>
            {showGeneral && (
              <Card.Body>
                <Tabs.Root defaultValue="profile">
                  <Tabs.List>
                    <Tabs.Trigger value="profile">
                      {t('customer.tabs.profile', undefined, 'Profile')}
                    </Tabs.Trigger>
                    <Tabs.Trigger value="address">
                      {t('customer.tabs.address', undefined, 'Address')}
                    </Tabs.Trigger>
                    {/* <Tabs.Trigger value="notification">Notification</Tabs.Trigger> */}
                  </Tabs.List>
                  <Tabs.Content value="profile">
                    <section className="flex flex-row justify-between items-start py-2 w-full overflow-hidden">
                      <div className="flex-1">
                        <DetailRenderer
                          resource="customer"
                          data={{
                            // TODO: member since field
                            name: `${firstName} ${lastName}`,
                            email: emailAddress,
                            mobileNumber: phoneNumber,
                            status,
                            dateOfBirth: dateOfBirth,
                            gender,
                            preferredOutlet: preferredOutletId,
                            signupThrough,
                            referralCode,
                            referralBy: referralCodeApplied
                              ? referralBy?.getCustomerByReferralCode
                              : null,
                            isProfileComplete,
                          }}
                          render={{
                            status: (val) => (
                              <Tag
                                className={`${CustomerStatusTagColor[status as CustomerStatus]}`}
                              >
                                {val}
                              </Tag>
                            ),
                            dateOfBirth: (birthdate) => {
                              if (!!birthdate) {
                                return <span>{dayjs(birthdate).format(FullDateFormat)}</span>;
                              }
                              return '-';
                            },

                            gender: (gender) => {
                              if (!!gender) {
                                return <span>{startCase(camelCase(gender))}</span>;
                              }
                              return '-';
                            },
                            referralBy: (customer: Customer) => {
                              if (!customer) return '-';

                              return (
                                <LinkToDetails
                                  resourceId={customer.id}
                                  resourceName={resourceNames.customer}
                                  highlight
                                >
                                  {formatFullName(customer.firstName, customer.lastName)}
                                </LinkToDetails>
                              );
                            },
                            preferredOutlet: (preferredOutletId) => {
                              if (!!preferredOutletId) {
                                const outlet = getOutletById(preferredOutletId);
                                return <span>{outlet?.name || preferredOutletId}</span>;
                              }
                              return '-';
                            },
                            isProfileComplete: (isProfileComplete) => {
                              return <Checkbox checked={!!isProfileComplete} disabled />;
                            },
                          }}
                          loading={isLoading}
                        />
                      </div>
                      <div className="px-4 flex-shrink-0">
                        <IconButton
                          onClick={() => navigateTo.edit(resourceNames.customer, customerId!)}
                        >
                          <FiEdit3 className="!text-success-300" size={25} />
                        </IconButton>
                      </div>
                    </section>
                  </Tabs.Content>
                  <Tabs.Content value="address">
                    <AddressContent
                      customerId={customerId}
                      customerFullName={fullName}
                      isLoading={isLoading}
                      sortedAddress={sortedAddress}
                      refetch={refetchAll}
                    />
                  </Tabs.Content>
                  {/* <Tabs.Content value="notification">
                    <DetailRenderer
                      ignoreTranslations={true}
                      resource="notificationBlasts"
                      loading={loadingNovuPreference}
                      data={customerPreferenceObj}
                      // render={Object.keys(customerPreferenceObj).forEach(key => ())}
                      render={notificationRender()}
                    />
                  </Tabs.Content> */}
                </Tabs.Root>
              </Card.Body>
            )}
          </Card>

          <Card className="relative z-0">
            <Card.Header
              className="font-bold cursor-pointer"
              bordered
              onClick={() => setShowOrder(!showOrder)}
            >
              <section className="flex flex-row items-center justify-between">
                <h1>{t('customer.details.orders', undefined, 'Orders')}</h1>
                <div className="flex flex-row space-x-2">
                  <BsChevronUp
                    size={20}
                    className={`transition-transform ${showOrder ? 'rotate-180' : 'rotate-0'}`}
                  />
                </div>
              </section>
            </Card.Header>
            {showOrder && (
              <Card.Body className="px-0 py-0">
                <CustomerOrder
                  loading={isLoading}
                  customerId={customerId}
                  customerOrderIds={orders?.items?.map((order: { id: string }) => order.id)}
                />
              </Card.Body>
            )}
          </Card>

          {/* Old Wallet Section - Kept for reference but disabled since wallet is now at the top */}
          {/* {isWalletEnabled && (
            <Card className="relative z-0">
              <Card.Header
                className="font-bold cursor-pointer"
                bordered
                onClick={() => setShowWallet(!showWallet)}
              >
                <section className="flex flex-row items-center justify-between">
                  <h1>{t('customer.details.wallet', undefined, 'Wallet')}</h1>
                  <div className="flex flex-row space-x-2">
                    <BsChevronUp
                      size={20}
                      className={`transition-transform ${showWallet ? 'rotate-180' : 'rotate-0'}`}
                    />
                  </div>
                </section>
              </Card.Header>
              {showWallet && (
                <Card.Body className="px-0 py-0">
                  <CustomerWallet
                    customerId={customerId}
                    loading={isLoading}
                  />
                </Card.Body>
              )}
            </Card>
          )} */}

          {/* TODO: unhide this after backend stable */}
          {/* Point & Redemption History */}
          <Card className="relative z-0">
            <Card.Header
              className="font-bold cursor-pointer"
              bordered
              onClick={() => setShowLoyalty(!showLoyalty)}
            >
              <section className="flex flex-row items-center justify-between">
                <h1>{t('customer.details.loyalty', undefined, 'Loyalty')}</h1>
                <div className="flex flex-row items-center space-x-2">
                  <Button
                    variant="solid"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation(); // prevent from triggering header's onclick
                      setOpenRedeemReward(true);
                    }}
                  >
                    {t('customer.loyalty.redeemReward')}
                  </Button>
                  <BsChevronUp
                    size={20}
                    className={`transition-transform ${showLoyalty ? 'rotate-180' : 'rotate-0'}`}
                  />
                </div>
              </section>
            </Card.Header>
            {showLoyalty && (
              <Card.Body className="px-0 py-0">
                <Tabs.Root defaultValue="redemption">
                  <Tabs.List>
                    <Tabs.Trigger value="redemption">
                      {t('customer.tabs.rewards', undefined, 'Rewards')}
                    </Tabs.Trigger>
                    <Tabs.Trigger value="points">
                      {t('customer.tabs.points', undefined, 'Points')}
                    </Tabs.Trigger>
                  </Tabs.List>
                  <Tabs.Content value="redemption">
                    <CustomerRedemptionHistory
                      loading={isLoading}
                      userId={customer?.data?.user?.id || ''}
                      refetchTrigger={membershipFetch}
                    />
                  </Tabs.Content>
                  <Tabs.Content value="points">
                    <CustomerPointTransactions
                      loading={isLoading}
                      userId={customer?.data?.user?.id || ''}
                      refetchTrigger={membershipFetch}
                    />
                  </Tabs.Content>
                </Tabs.Root>
              </Card.Body>
            )}
          </Card>
        </main>
      </ShowPageWrapper>
    </div>
  );
};
